import videojs from 'video.js';
const Component = videojs.getComponent('Component');
const ModalDialog = videojs.getComponent('ModalDialog');

const dom = videojs.dom || videojs;

const DEFAULTS = {
	buttonToggle: null
};

/**
 * @typedef {Object} Options
 * @property {boolean} isAvailable
 * @property {Array<Number>} restrictedTrackIds
 */
class AudiotracksSettings extends ModalDialog {
	/**
	 * @param {videojs.Player} player
	 * @param {Options} options
	 */
	constructor(player, options) {
		let options_ = Object.assign({}, DEFAULTS, options); 
		options_.uncloseable = true;
		super(player, options_);
		this.hide();
		this.init();
	}

	setListeners_() {
		if (this.options_.isMatchTVMode) {
			function handleClickByField(item) {
				if (this.options_.isTrackRestricted(item.value)) {
					this.options_.showRestrictionModalDialog(() => {
						this.options_.requestAccess({accessTo: 'quality', quality: item.value});
					});
					this.hide();
					return;
				}

				item.checked = true;
				this.options_.audiotracksList.play(item.value);
				if (this.options_.buttonToggle) {
					this.options_.buttonToggle.toggleClass('vjs-audiotracks-control_cross-out', item.value === 'a_noise');
				}
				this.hide();
				this.options_.buttonToggle.removeClass('vjs-audiotracks-settings-show');
			};
			let fields_items = this.$$('input[name="vjs_audiotracks_settings_type"]');
			for (let item of fields_items) {
				const _handleClickByField = handleClickByField.bind(this, item);
				item.closest('.vjs-audiotracks-settings-field').addEventListener('click', handleClickByField.bind(this, item));
				item.closest('.vjs-audiotracks-settings-field').addEventListener('touchstart', (startEvent) => {
					if (startEvent.touches.length > 1) return;
					const startScrollTop = this.el().scrollTop;
					startEvent.target.addEventListener(
						'touchend',
						(endEvent) => {
							const isLongTouch = endEvent.timeStamp - startEvent.timeStamp > 100;
							const isBigShift = Math.abs(startScrollTop - this.el().scrollTop) > 5;
							if (isLongTouch || isBigShift) return;
							_handleClickByField();
						}, {
							once: true
						}
					);
				});
			}

			this.player().on('playerresize', () => this.adjustHeight());
		} else {
			this.on(this.$('.vjs-audiotracks-settings-close'), 'click', () => {
				this.hide();
			});
			this.on(this.$('.vjs-audiotracks-settings-accept'), 'click', () => {
				let selectedId = this.$('input[name="vjs_audiotracks_settings_type"]:checked').value;
				this.options_.audiotracksList.play(selectedId);
				this.hide();
			});
		}
	}

	init() {
		if (this.options_.baseFontSizeInModal) {
			this.el().style.fontSize = this.options_.baseFontSizeInModal;
		}
		this.fillWith(this.createContent());
		this.setListeners_();
		this.setIcon();
	}

	setIcon() {
		if (this.options_.buttonToggle) {
			this.options_.buttonToggle.toggleClass('vjs-audiotracks-control_cross-out', !!this.options_.audiotracksList.isSelected('a_noise'));
		}
	}

	createContent() {
		let els = [];
		els.push(this.createClose_());
		els.push(this.createHeading_());
		els.push(this.createFields_());
		if (!this.options_.isMatchTVMode) {
			els.push(this.createButtons_());
		}
		return els;
	}

	buildCSSClass() {
		return `vjs-audiotracks-settings ${super.buildCSSClass()}`;
	}

	createClose_() {
		let el = dom.createEl('span', {
			className: 'vjs-audiotracks-settings-close'
		});
		if (!this.options_.isMatchTVMode) {
			dom.insertContent(el, '\u00D7');
		}
		return el;
	}

	createHeading_() {
		let heading = dom.createEl('div', {
			className: 'vjs-audiotracks-settings-caption'
		}, {
			'role': 'heading'
		});
		dom.insertContent(heading, this.localize(this.options_.title ? this.options_.title : "Audiotracks"));
		return heading;
	}

	createField_(track) {
		const isTrackRestricted = this.options_.isTrackRestricted && this.options_.isTrackRestricted(track.id);

		let fld = dom.createEl('div', {
			className: 'vjs-audiotracks-settings-field'
		});

		let radio = dom.createEl('input', {
			className: 'vjs-audiotracks-settings-field-radio vjs-hidden',
			id: `vjs_audiotracks_settings_field_${track.id}`,
			name: `vjs_audiotracks_settings_type`
		}, {
			type: 'radio',
			value: track.id
		});
		radio.checked = this.options_.audiotracksList.isSelected(track.id);

		let label = dom.createEl('label', {
			className: 'vjs-audiotracks-settings-field-label ' + (isTrackRestricted ? 'vjs-audiotracks-settings-field-label_restricted' : ''),
		});

		if (!isTrackRestricted) {
			label.setAttribute('for', `vjs_audiotracks_settings_field_${track.id}`);
		}

		dom.appendContent(label, track.title);
		dom.appendContent(fld, [radio, label]);
		return fld;
	}

	createFields_() {
		let infoFields = dom.createEl('div', {
			className: 'vjs-audiotracks-settings-fields'
		});

		this.options_.audiotracksList.items.forEach((t) => {
			dom.appendContent(infoFields, this.createField_(t));
		});
		return infoFields;
	}

	createButtons_() {
		let btn = dom.createEl('input', {
			className: 'vjs-audiotracks-settings-accept',
			value: this.localize('Accept audiotracks settings')
		}, {
			type: 'button'
		});
		return btn;
	}

	adjustPosition() {
		if (this.options_.isMatchTVMode) {
			return;
		}
		const isFullscreen = document.fullscreenElement === this.player().el();
		const isMobileView = this.checkMobileView();
		if (isFullscreen && isMobileView) {
			const {
				offsetTop: controlBarOffsetTop,
				offsetLeft: controlBarOffsetLeft,
				clientHeight: controlBarHeight
			} = this.player().getChild('controlBar').el();
			this.el().style.width = this.player().currentWidth() + 'px';
			this.el().style.left = `${-this.options_.buttonToggle.el().offsetLeft - controlBarOffsetLeft}px`;
			this.el().style.bottom = `${controlBarOffsetTop + controlBarHeight - this.player().currentHeight()}px`;
		} else if (isMobileView) {
			this.el().style.width = this.player().currentWidth() + 'px';
			if (this.options_.buttonToggle) {
				this.el().style.left = -this.options_.buttonToggle.el().offsetLeft + 'px';
			}
			this.el().style.bottom = '';
		} else {
			this.el().style.width = '';
			this.el().style.left = '';
			this.el().style.bottom = '';
		}
	}

	checkMobileView() {
		return this.player().currentWidth() <= 812;
	}

	adjustHeight() {
		if (!this.options_.isMatchTVMode) {
			return;
		}
		const topIndent = 12;
		const bottomIndent = topIndent;
		this.el().style.maxHeight = this.player().getChild('controlBar').el().offsetTop - topIndent - bottomIndent + 'px';
	}

	show() {
		super.show();
		this.adjustPosition();
		this.adjustHeight();
		if (this.checkMobileView()) {
			// timeout нужен чтобы при одновременном закрытии одного меню и открытии этого добавление класса было после его удаления
			setTimeout(() => {
				this.player().addClass('vjs-showing-settings');
			});
		}
		this.options_.audiotracksList.items.forEach((item) => {
			const isTrackRestricted = this.options_.isTrackRestricted ? this.options_.isTrackRestricted(item.id) : false;
			if (this.options_.audiotracksList.isSelected(item.id) && !isTrackRestricted) {
				this.$(`[id ='vjs_audiotracks_settings_field_${item.id}']`).checked = true;
			}
		});
	}

	hide() {
		super.hide();
		if (this.checkMobileView()) {
			this.player().removeClass('vjs-showing-settings');
		}
	}
}

Component.registerComponent('AudiotracksSettings', AudiotracksSettings);
export default AudiotracksSettings;
